import * as React from 'react'
import * as PropTypes from 'prop-types'
import { css } from 'glamor'
import moment from 'moment'

import { Touchable } from '../touchable'
import { Theme } from '../../theme'

interface IDatepickerDateProps {
  timeperiod: {
    fromDate?: moment.Moment | null
    toDate?: moment.Moment | null
  }
  date: moment.Moment
  onDateSelected: (e: React.SyntheticEvent) => void
  isFromMonth?: boolean
  isSingleMonth?: boolean
  firstSelectableDate?: moment.Moment
  lastSelectableDate?: moment.Moment
  isEmptyPeriod?: boolean
  isOtherMonth?: boolean
  format?: string
}

export class DatepickerDate extends React.Component<IDatepickerDateProps> {
  static propTypes = {
    timeperiod: PropTypes.object.isRequired,
    date: PropTypes.object.isRequired,
    onDateSelected: PropTypes.func.isRequired,
    isFromMonth: PropTypes.bool.isRequired,
    isSingleMonth: PropTypes.bool,
    firstSelectableDate: PropTypes.object,
    lastSelectableDate: PropTypes.object,
    isEmptyPeriod: PropTypes.bool,
    isOtherMonth: PropTypes.bool,
    format: PropTypes.string,
  }

  static defaultProps = { isSingleMonth: false, format: 'D' }

  onDateClicked = (e: React.SyntheticEvent) => {
    if (!this.isDisabled()) {
      return this.props.onDateSelected(e)
    }
  }

  isSelected() {
    const {
      date,
      timeperiod: { fromDate, toDate },
    } = this.props

    return (
      (!fromDate && toDate && date.isBefore(toDate)) ||
      (!toDate && fromDate && date.isAfter(fromDate)) ||
      (toDate && date.isBefore(toDate) && fromDate && date.isAfter(fromDate)) ||
      this.isFirstSelected() ||
      this.isLastSelected()
    )
  }

  isFirstSelected() {
    return this.props.timeperiod.fromDate != null && this.props.date.isSame(this.props.timeperiod.fromDate)
  }

  isLastSelected() {
    return this.props.timeperiod.toDate != null && this.props.date.isSame(this.props.timeperiod.toDate)
  }

  isDisabled() {
    const {
      date,
      isSingleMonth,
      isEmptyPeriod,
      timeperiod: { fromDate, toDate },
      isFromMonth,
      firstSelectableDate,
      lastSelectableDate,
    } = this.props

    if (isSingleMonth || isEmptyPeriod) {
      return (
        (firstSelectableDate && date.isBefore(firstSelectableDate)) ||
        (lastSelectableDate && date.isAfter(lastSelectableDate)) ||
        false
      )
    } else if (isFromMonth) {
      return (
        (toDate && date.isAfter(toDate)) ||
        (firstSelectableDate && date.isBefore(firstSelectableDate)) ||
        (lastSelectableDate && date.isAfter(lastSelectableDate))
      )
    }
    return (fromDate && date.isBefore(fromDate)) || (lastSelectableDate && date.isAfter(lastSelectableDate))
  }

  isActive() {
    const { date, timeperiod, isFromMonth } = this.props

    if (isFromMonth) {
      if (!timeperiod.fromDate) return false
      return date.isSame(timeperiod.fromDate, 'day')
    }
    if (!timeperiod.toDate) return false
    return date.isSame(timeperiod.toDate, 'day')
  }

  render() {
    const { date } = this.props

    return (
      <Theme>
        {theme => {
          const base = {
            cursor: 'pointer',
            marginBottom: '5px',
            fontWeight: '700',
            height: '40px',
            lineHeight: '40px',
            textAlign: 'center',
            margin: '0',
            color: `${theme.colors.accents.primary}`,
            transition: 'all 0.15s cubic-bezier(0.23, 1, 0.32, 1)',
            ':hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
            ':disabled:active, :disabled:hover': { backgroundColor: 'transparent' },
            ':nth-child(7n)': {
              borderTopRightRadius: '0px',
              borderBottomRightRadius: '0px',
            },
            ':nth-child(7n-6)': {
              borderTopLeftRadius: '0px',
              borderBottomLeftRadius: '0px',
            },
            '.intent-mouse &:focus': {
              outline: 'none',
            },
          }
          const selected = {
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
            borderTop: '0 rgba(0, 0, 0, 0.15) solid',
            borderBottom: '0 rgba(0, 0, 0, 0.15) solid',
            ':nth-child(7n)': {
              borderRight: '0 rgba(0, 0, 0, 0.15) solid',
            },
            ':nth-child(7n-6)': {
              borderLeft: '0 rgba(0, 0, 0, 0.15) solid',
            },
            ':hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
            },
          }
          const selectedFirst = { border: `0 ${theme.colors.accents.primary} solid` }
          const selectedLast = selectedFirst
          const active = {
            backgroundColor: `${theme.colors.accents.primary}`,
            color: 'white',
            ':hover': {
              backgroundColor: `${theme.colors.accents.primary}`,
              opacity: '0.8',
            },
          }
          const otherMonth = { opacity: '.4' }
          const disabled = {
            cursor: 'default',
            color: '#999999',
            ':hover': {
              backgroundColor: 'inherit',
            },
          }
          const notSelected = { borderRadius: '0px' }

          return (
            <Touchable
              {...css(
                base,
                this.isSelected() ? selected : notSelected,
                this.isDisabled() && disabled,
                this.isFirstSelected() && selectedFirst,
                this.isLastSelected() && selectedLast,
                this.isActive() && active,
                this.props.isOtherMonth && otherMonth
              )}
              data-active={!!this.isActive()}
              data-date={date.format()}
              data-disabled={!!this.isDisabled()}
              data-formatted-date={date.format('YYYY-MM-DD')}
              data-selected={!!this.isSelected()}
              data-selected-first={!!this.isFirstSelected()}
              data-selected-last={!!this.isLastSelected()}
              onClick={this.onDateClicked}
            >
              {date.format(this.props.format)}
            </Touchable>
          )
        }}
      </Theme>
    )
  }
}
